/**
 * pages/AdminListingsTab.jsx — v2.71.100
 * ---------------------------------------
 * Server-side filterable + paginated listings table for the admin
 * console. Replaces the legacy client-side filter inside AdminPage.jsx
 * (which loaded the entire `/properties` feed in memory).
 *
 * Filter set
 * ──────────
 *   • Free-text search (title / property id / dealer phone / dealer name)
 *   • Status — Active / Pending / Sold / Rejected / Approved / Draft
 *   • City (main_area) — auto-populated from DB
 *   • Locality (sub_area) — auto-populated, narrowed by city
 *   • Submitter (dealer / builder / owner) — auto-populated typeahead
 *   • Property type — auto-populated
 *   • Featured toggle
 *   • Sort — Newest · Oldest · Price ↑ · Price ↓ · Title A→Z
 *   • Pagination — 25 / 50 / 100 / 200 per page
 *
 * Data source: GET /api/admin/listings (server-paginated, projection-trimmed)
 * Filter options:    GET /api/admin/listings/filter-options
 *
 * Parent (AdminPage) hands us actions so the legacy edit/delete/approve
 * flows continue to work and refresh the dashboard counts.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search, X, RefreshCw, ChevronLeft, ChevronRight, Pencil, Trash2, CheckCircle2,
  Ban, Star, Eye, Package, IndianRupee, MapPin, Filter,
} from "lucide-react";
import api, { formatINR } from "../lib/api";
import { useDataSync, useAutoRefresh } from "../lib/dataSync";
// v2.80 — Mirror filters / sort / page into the URL so back-from-detail
// restores the exact list view. Hook is type-safe (number/bool coerce)
// and omits defaults from the URL for clean shareable links.
import { useUrlStateMulti } from "../shared/utils/useUrlState";
import { TableBodySkeleton, ListSkeleton } from "../components/Skeletons";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
// v2.71.102 — Restore card-view toggle that was removed in v2.71.100.
// Cards are the mobile-first / image-led view; table is the dense / scannable
// view for power users. Default to cards (preserves the long-standing UX).
import PropertyCard from "../shared/components/PropertyCard";
import ViewToggle, { useViewToggle } from "../shared/components/ViewToggle";

const STATUS_OPTIONS = [
  { value: "all",      label: "All status" },
  { value: "active",   label: "Active" },
  { value: "pending",  label: "Pending review" },
  { value: "approved", label: "Approved (all)" },
  { value: "sold",     label: "Sold / Rented" },
  { value: "rejected", label: "Rejected" },
  { value: "draft",    label: "Draft" },
];

const SORT_OPTIONS = [
  { value: "newest",     label: "Newest first" },
  { value: "oldest",     label: "Oldest first" },
  { value: "price_desc", label: "Price: High → Low" },
  { value: "price_asc",  label: "Price: Low → High" },
  { value: "title_asc",  label: "Title: A → Z" },
];

const LIMIT_OPTIONS = [25, 50, 100, 200];

function useDebouncedValue(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function fmtDateShort(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" });
  } catch { return iso; }
}

function StatusPill({ p }) {
  if (["Sold", "Rented"].includes(p.status)) {
    return <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm bg-[#DBEAFE] text-[#1D4ED8]">SOLD</span>;
  }
  const a = (p.approval_status || "").toLowerCase();
  if (a === "approved") return <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm bg-[#DCFCE7] text-[#15803D]">ACTIVE</span>;
  if (a === "pending")  return <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm bg-[#FEF3C7] text-[#92400E]">PENDING</span>;
  if (a === "rejected") return <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm bg-[#FEE2E2] text-[#991B1B]">REJECTED</span>;
  if (a === "draft")    return <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm bg-[#F4F4F5] text-[#52525B]">DRAFT</span>;
  return <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm bg-[#F4F4F5] text-[#52525B]">{(p.approval_status || "—").toUpperCase()}</span>;
}

export default function AdminListingsTab({ onEdit, onApprove, onReject, onMarkSold, onMarkAvailable, onDelete, onToggleFeatured }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState({ main_areas: [], sub_areas: [], property_types: [], submitters: [] });

  // v2.71.102 — Card/Table view toggle. Default = cards.
  const [view, setView] = useViewToggle("admin-listings", "cards");

  // Filters — v2.80: every filter mirrors into the URL so the back
  // button from a property detail page restores the exact view. We
  // use the MULTI-key variant so a single mount issues at most one
  // URL write (vs N separate writes with per-field hooks, which
  // historically caused race conditions where the URL was rebuilt
  // mid-render and dropped params).
  const filterState = useUrlStateMulti({
    q: "",
    status: "all",
    city: "",
    locality: "",
    submitter: "",
    ptype: "",
    featured: false,
    sort: "newest",
    page: 1,
    limit: 50,
  });
  const { values: f, set: setF } = filterState;
  const search = f.q;
  const setSearch = useCallback((v) => setF("q", v), [setF]);
  const status = f.status;
  const setStatus = useCallback((v) => setF("status", v), [setF]);
  const city = f.city;
  const setCity = useCallback((v) => setF("city", v), [setF]);
  const locality = f.locality;
  const setLocality = useCallback((v) => setF("locality", v), [setF]);
  const submitterId = f.submitter;
  const setSubmitterId = useCallback((v) => setF("submitter", v), [setF]);
  const propType = f.ptype;
  const setPropType = useCallback((v) => setF("ptype", v), [setF]);
  const featuredOnly = f.featured;
  const setFeaturedOnly = useCallback((v) => setF("featured", v), [setF]);
  const sort = f.sort;
  const setSort = useCallback((v) => setF("sort", v), [setF]);
  const page = f.page;
  const setPage = useCallback((v) => setF("page", v), [setF]);
  const limit = f.limit;
  const setLimit = useCallback((v) => setF("limit", v), [setF]);

  const debouncedSearch = useDebouncedValue(search, 300);

  // v2.80 — On filter change, reset to page 1. Use a signature ref so:
  //   • StrictMode's simulated remount doesn't trip the guard (ref
  //     survives the remount with stale value).
  //   • Background dataSync updates that don't change filter values
  //     don't pointlessly reset pagination.
  //   • URL-supplied `page=N` survives mount because the very first
  //     run only records the signature and exits.
  const lastFilterSigRef = useRef(null);
  useEffect(() => {
    const sig = JSON.stringify([debouncedSearch, status, city, locality, submitterId, propType, featuredOnly, sort, limit]);
    if (lastFilterSigRef.current === null || lastFilterSigRef.current === sig) {
      lastFilterSigRef.current = sig;
      return;
    }
    lastFilterSigRef.current = sig;
    setPage(1);
  }, [debouncedSearch, status, city, locality, submitterId, propType, featuredOnly, sort, limit, setPage]);

  // Load filter options once
  useEffect(() => {
    let cancel = false;
    api.get("/admin/listings/filter-options").then((r) => {
      if (!cancel) setOptions(r.data || {});
    }).catch(() => {});
    return () => { cancel = true; };
  }, []);

  // Locality options narrow when city is selected (fall back to all when no city)
  const localityOptions = useMemo(() => {
    return options.sub_areas || [];
  }, [options.sub_areas]);

  // Main fetch
  const reqIdRef = useRef(0);
  // v2.72 — `silent === true` skips the loading flash; used for background
  // refreshes (dataSync invalidation + 45 s auto-refresh) so rows never
  // blink away. Strict-equality guard because the Refresh button passes the
  // click event as the first argument.
  const fetchPage = useCallback(async (silent = false) => {
    const myReq = ++reqIdRef.current;
    if (silent !== true) setLoading(true);
    try {
      const params = { sort, page, limit };
      if (debouncedSearch.trim()) params.q = debouncedSearch.trim();
      if (status && status !== "all") params.status = status;
      if (city) params.main_area = city;
      if (locality) params.sub_area = locality;
      if (submitterId) params.submitted_by_id = submitterId;
      if (propType) params.property_type = propType;
      if (featuredOnly) params.featured = true;
      const r = await api.get("/admin/listings", { params });
      if (myReq !== reqIdRef.current) return;
      setItems(r.data.results || []);
      setTotal(r.data.total || 0);
      setTotalPages(r.data.total_pages || 1);
    } catch {
      if (myReq !== reqIdRef.current) return;
      setItems([]); setTotal(0); setTotalPages(1);
    } finally {
      if (myReq === reqIdRef.current) setLoading(false);
    }
  }, [debouncedSearch, status, city, locality, submitterId, propType, featuredOnly, sort, page, limit]);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  // v2.72 — Live sync: instantly patch/remove the affected row when any
  // mutation announces it (optimistic feel), then silently re-fetch to
  // reconcile with the server. A 45 s visibility-aware background refresh
  // keeps long-lived admin sessions fresh without manual reloads.
  useDataSync(["properties"], (domains, payload) => {
    if (payload?.id) {
      if (payload.action === "delete") {
        setItems((prev) => prev.filter((x) => x.id !== payload.id));
        setTotal((t) => Math.max(0, t - 1));
      } else if (payload.action === "patch" && payload.patch) {
        setItems((prev) => prev.map((x) => (x.id === payload.id ? { ...x, ...payload.patch } : x)));
      }
    }
    fetchPage(true);
  });
  useAutoRefresh(() => fetchPage(true), 45000);

  const hasActiveFilter = !!(debouncedSearch || (status && status !== "all") || city || locality || submitterId || propType || featuredOnly);
  const clearAll = () => {
    setSearch(""); setStatus("all"); setCity(""); setLocality("");
    setSubmitterId(""); setPropType(""); setFeaturedOnly(false); setSort("newest");
  };

  return (
    <div className="space-y-3" data-testid="admin-listings-tab">
      {/* ─── Filter toolbar ────────────────────────────────────────── */}
      <div className="bg-white border border-[#E4E4E7] rounded-sm p-3" data-testid="admin-listings-filters">
        <div className="flex flex-col lg:flex-row lg:items-center gap-2">
          {/* Search */}
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
            <Input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by ID, title, phone, dealer name…"
              className="h-9 pl-8 pr-8 text-[13px]"
              data-testid="admin-listings-search"
            />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-[#27272A]" data-testid="admin-listings-search-clear">
                <X size={13} />
              </button>
            )}
          </div>
          {/* Status */}
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 rounded-sm border border-[#E4E4E7] bg-white px-2 text-[13px]" data-testid="admin-listings-status">
            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {/* City */}
          <select value={city} onChange={(e) => setCity(e.target.value)} className="h-9 rounded-sm border border-[#E4E4E7] bg-white px-2 text-[13px] min-w-[140px]" data-testid="admin-listings-city">
            <option value="">All cities</option>
            {(options.main_areas || []).map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          {/* Locality */}
          <select value={locality} onChange={(e) => setLocality(e.target.value)} className="h-9 rounded-sm border border-[#E4E4E7] bg-white px-2 text-[13px] min-w-[140px]" data-testid="admin-listings-locality">
            <option value="">All localities</option>
            {localityOptions.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          {/* Submitter */}
          <select value={submitterId} onChange={(e) => setSubmitterId(e.target.value)} className="h-9 rounded-sm border border-[#E4E4E7] bg-white px-2 text-[13px] min-w-[150px]" data-testid="admin-listings-submitter">
            <option value="">All owners</option>
            {(options.submitters || []).map((u) => (
              <option key={u.id} value={u.id}>{u.name || u.email || u.id.slice(0, 8)} ({u.role})</option>
            ))}
          </select>
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-2 pt-2 border-t border-[#F4F4F5]">
          {/* Property type */}
          <select value={propType} onChange={(e) => setPropType(e.target.value)} className="h-8 rounded-sm border border-[#E4E4E7] bg-white px-2 text-[12px] min-w-[140px]" data-testid="admin-listings-type">
            <option value="">Any type</option>
            {(options.property_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          {/* Featured toggle */}
          <label className="inline-flex items-center gap-1.5 text-[12px] text-[#27272A] cursor-pointer">
            <input type="checkbox" checked={featuredOnly} onChange={(e) => setFeaturedOnly(e.target.checked)} className="h-3.5 w-3.5" data-testid="admin-listings-featured" />
            <Star size={11} className="text-[#F59E0B]" /> Featured only
          </label>
          {/* Sort */}
          <div className="ml-auto flex items-center gap-2">
            <label className="text-[11px] uppercase tracking-wider font-semibold text-[#52525B]">Sort:</label>
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="h-8 rounded-sm border border-[#E4E4E7] bg-white px-2 text-[12px]" data-testid="admin-listings-sort">
              {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <label className="text-[11px] uppercase tracking-wider font-semibold text-[#52525B]">Per page:</label>
            <select value={limit} onChange={(e) => setLimit(Number(e.target.value))} className="h-8 rounded-sm border border-[#E4E4E7] bg-white px-2 text-[12px]" data-testid="admin-listings-limit">
              {LIMIT_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>

        {hasActiveFilter && (
          <div className="flex flex-wrap items-center gap-2 mt-2 pt-2 border-t border-[#F4F4F5]" data-testid="admin-listings-active-filters">
            <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#52525B] inline-flex items-center gap-1"><Filter size={10} /> Active:</span>
            {debouncedSearch && <FilterChip onClear={() => setSearch("")}>q: {debouncedSearch}</FilterChip>}
            {status !== "all" && <FilterChip onClear={() => setStatus("all")}>status: {status}</FilterChip>}
            {city && <FilterChip onClear={() => setCity("")}>city: {city}</FilterChip>}
            {locality && <FilterChip onClear={() => setLocality("")}>locality: {locality}</FilterChip>}
            {submitterId && <FilterChip onClear={() => setSubmitterId("")}>owner</FilterChip>}
            {propType && <FilterChip onClear={() => setPropType("")}>type: {propType}</FilterChip>}
            {featuredOnly && <FilterChip onClear={() => setFeaturedOnly(false)}>featured</FilterChip>}
            <button onClick={clearAll} className="text-[11.5px] font-semibold text-[#DC2626] hover:underline" data-testid="admin-listings-clear-all">Clear all</button>
          </div>
        )}
      </div>

      {/* ─── Header row: count + view-toggle + refresh ─────────────── */}
      <div className="flex items-center justify-between gap-2">
        <div className="text-[12px] text-[#52525B]" data-testid="admin-listings-count">
          {loading ? "Loading…" : (
            <>
              <span className="font-semibold text-[#09090B]">{total.toLocaleString("en-IN")}</span> {total === 1 ? "listing" : "listings"}
              {totalPages > 1 && <span className="text-[#A1A1AA]"> · Page {page} of {totalPages}</span>}
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* v2.71.102 — Card/Table view toggle */}
          <ViewToggle value={view} onChange={setView} testidPrefix="admin-listings-view" />
          <button onClick={fetchPage} className="inline-flex items-center gap-1 text-[12px] text-[#52525B] hover:text-[#0055FF]" data-testid="admin-listings-refresh">
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* ─── Cards (default) ───────────────────────────────────────── */}
      {view === "cards" && (
        <div className="space-y-2" data-testid="admin-listings-cards">
          {loading && (
            <div data-testid="admin-listings-cards-loading"><ListSkeleton rows={6} /></div>
          )}
          {!loading && items.length === 0 && (
            <div className="p-10 text-center text-[#A1A1AA] bg-white border border-dashed border-[#E4E4E7] rounded-sm" data-testid="admin-listings-cards-empty">
              {hasActiveFilter ? "No listings match the current filters." : "No listings yet."}
            </div>
          )}
          {!loading && items.map((p) => (
            <PropertyCard
              key={p.id}
              property={p}
              variant="admin"
              onOpen={(prop) => {
                // v2.71.101 — pending / draft / rejected open the Approval
                // Review screen so the admin can act on them; approved/sold
                // open the public preview.
                // v2.75 — same-tab navigation (was opening new tab). Admins can
                // still Cmd/Ctrl+Click any Link for browser-native new-tab override.
                const a = (prop.approval_status || "").toLowerCase();
                if (a === "pending" || a === "draft" || a === "rejected") {
                  window.location.href = `/admin/approvals?id=${prop.id}`;
                } else {
                  window.location.href = `/property/${prop.id}`;
                }
              }}
              onToggleFeatured={onToggleFeatured}
              onMarkSold={(id) => onMarkSold?.(id)}
              onMarkAvailable={(id) => onMarkAvailable?.(id)}
              onEdit={onEdit}
              onDelete={(id) => onDelete?.(id)}
              testidPrefix="admin-listing-card"
            />
          ))}
        </div>
      )}

      {/* ─── Table (legacy / dense view) ───────────────────────────── */}
      {view === "table" && (
      <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-x-auto" data-testid="admin-listings-scroll">
        <table className="w-full text-sm min-w-[920px]">
          <thead className="bg-[#F4F4F5] border-b border-[#E4E4E7]">
            <tr className="text-left text-[10px] uppercase tracking-wider text-[#52525B]">
              <th className="p-3 font-semibold">ID</th>
              <th className="p-3 font-semibold">Property</th>
              <th className="p-3 font-semibold">Status</th>
              <th className="p-3 font-semibold text-right">Price</th>
              <th className="p-3 font-semibold">Owner</th>
              <th className="p-3 font-semibold">Created</th>
              <th className="p-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <TableBodySkeleton rows={8} cols={7} />}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="p-10 text-center text-[#A1A1AA]" data-testid="admin-listings-empty">
                {hasActiveFilter ? "No listings match the current filters." : "No listings yet."}
              </td></tr>
            )}
            {!loading && items.map((p) => {
              const isPending = p.approval_status === "pending";
              const isSold = ["Sold", "Rented"].includes(p.status);
              const isFeatured = !!(p.is_featured || p.featured || p.premium_deal || p.priority || p.best_deal);
              return (
                <tr key={p.id} className="border-b border-[#F4F4F5] hover:bg-[#FAFAFA]" data-testid={`admin-listing-row-${p.id}`}>
                  <td className="p-3 font-mono-data text-[11px] text-[#52525B] whitespace-nowrap" title={p.id}>{String(p.id).slice(0, 8)}…</td>
                  <td className="p-3">
                    {/* v2.71.101 — Pending/draft/rejected listings open the
                        Approval Review screen (deep-linked), not the public
                        Property Detail page (which 404s for non-admin and
                        defeats the approval workflow). Approved/sold rows
                        keep linking to the public detail as before. */}
                    {isPending || p.approval_status === "draft" || p.approval_status === "rejected" ? (
                      <Link to={`/admin/approvals?id=${p.id}`} className="text-[13px] font-medium text-[#92400E] hover:underline line-clamp-1" data-testid={`admin-listing-title-${p.id}`}>
                        {p.title || "Untitled"}
                      </Link>
                    ) : (
                      <Link to={`/property/${p.id}`} className="text-[13px] font-medium text-[#0055FF] hover:underline line-clamp-1" data-testid={`admin-listing-title-${p.id}`}>
                        {p.title || "Untitled"}
                      </Link>
                    )}
                    <div className="text-[11px] text-[#71717A] mt-0.5 flex items-center gap-2 flex-wrap">
                      {p.bhk && <span>{p.bhk}</span>}
                      {p.property_type && <span>· {p.property_type}</span>}
                      {(p.sub_area || p.main_area) && <span className="inline-flex items-center gap-0.5">· <MapPin size={9} /> {p.sub_area || p.main_area}</span>}
                      {isFeatured && <Star size={10} className="text-[#F59E0B] fill-current" />}
                    </div>
                  </td>
                  <td className="p-3"><StatusPill p={p} /></td>
                  <td className="p-3 text-right font-semibold text-[#0055FF] whitespace-nowrap inline-flex items-center justify-end gap-0.5">
                    <IndianRupee size={10} />{(formatINR(p.price) || "—").replace("₹", "")}
                  </td>
                  <td className="p-3 text-[12px] text-[#52525B]">
                    {p.dealer_name ? (
                      <Link to={`/admin/users/${p.created_by}`} className="hover:text-[#0055FF] hover:underline truncate inline-block max-w-[140px]" title={p.dealer_name}>
                        {p.dealer_name}
                      </Link>
                    ) : p.created_by ? (
                      <Link to={`/admin/users/${p.created_by}`} className="hover:text-[#0055FF] hover:underline">{String(p.created_by).slice(0,8)}…</Link>
                    ) : <span className="text-[#A1A1AA]">—</span>}
                    {p.submitted_by_role && <div className="text-[10px] uppercase tracking-wider text-[#A1A1AA]">{p.submitted_by_role}</div>}
                  </td>
                  <td className="p-3 text-[11px] text-[#71717A] whitespace-nowrap">{fmtDateShort(p.created_at)}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-0.5">
                      {/* v2.75 — same-tab nav (was target="_blank"). Browser
                          back returns user to /admin/listings with the same
                          filter+page state preserved. Cmd/Ctrl+Click still
                          opens a new tab via the browser's native behaviour. */}
                      <Link to={`/property/${p.id}`} className="h-7 w-7 rounded inline-flex items-center justify-center hover:bg-[#F4F4F5] text-[#52525B]" title="View" data-testid={`admin-listing-view-${p.id}`}>
                        <Eye size={13} />
                      </Link>
                      <button onClick={() => onToggleFeatured?.(p)} className={`h-7 w-7 rounded inline-flex items-center justify-center ${isFeatured ? "bg-[#FEF3C7] text-[#F59E0B]" : "hover:bg-[#F4F4F5] text-[#71717A]"}`} title={isFeatured ? "Unfeature" : "Mark Featured"} data-testid={`admin-listing-feature-${p.id}`}>
                        <Star size={13} fill={isFeatured ? "#F59E0B" : "none"} />
                      </button>
                      {isPending && (
                        <>
                          <button onClick={() => onApprove?.(p.id)} className="h-7 w-7 rounded inline-flex items-center justify-center hover:bg-[#DCFCE7] text-[#15803D]" title="Approve" data-testid={`admin-listing-approve-${p.id}`}>
                            <CheckCircle2 size={13} />
                          </button>
                          <button onClick={() => onReject?.(p.id)} className="h-7 w-7 rounded inline-flex items-center justify-center hover:bg-[#FEE2E2] text-[#DC2626]" title="Reject" data-testid={`admin-listing-reject-${p.id}`}>
                            <Ban size={13} />
                          </button>
                        </>
                      )}
                      {isSold ? (
                        <button onClick={() => onMarkAvailable?.(p.id)} className="h-7 w-7 rounded inline-flex items-center justify-center hover:bg-[#F4F4F5] text-[#52525B]" title="Mark Available" data-testid={`admin-listing-available-${p.id}`}>
                          <Package size={13} />
                        </button>
                      ) : (
                        <button onClick={() => onMarkSold?.(p.id)} className="h-7 w-7 rounded inline-flex items-center justify-center hover:bg-[#DBEAFE] text-[#1D4ED8]" title="Mark Sold" data-testid={`admin-listing-sold-${p.id}`}>
                          <CheckCircle2 size={13} />
                        </button>
                      )}
                      <button onClick={() => onEdit?.(p)} className="h-7 w-7 rounded inline-flex items-center justify-center hover:bg-[#F4F4F5] text-[#52525B]" title="Edit" data-testid={`admin-listing-edit-${p.id}`}>
                        <Pencil size={13} />
                      </button>
                      <button onClick={() => onDelete?.(p.id)} className="h-7 w-7 rounded inline-flex items-center justify-center hover:bg-[#FEE2E2] text-[#DC2626]" title="Delete" data-testid={`admin-listing-delete-${p.id}`}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}

      {/* ─── Pagination footer ─────────────────────────────────────── */}
      {totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-2 px-1 py-2" data-testid="admin-listings-pagination">
          <div className="text-[11.5px] text-[#52525B]">
            Showing <span className="font-semibold text-[#09090B]">{(page - 1) * limit + 1}–{Math.min(page * limit, total)}</span> of {total.toLocaleString("en-IN")}
          </div>
          <div className="inline-flex items-center gap-1">
            <Button size="sm" variant="outline" disabled={page <= 1 || loading} onClick={() => setPage(1)} className="h-7 text-[11.5px] rounded-sm" data-testid="admin-listings-first">
              First
            </Button>
            <Button size="sm" variant="outline" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))} className="h-7 text-[11.5px] rounded-sm" data-testid="admin-listings-prev">
              <ChevronLeft size={12} /> Prev
            </Button>
            <span className="px-2 text-[11.5px] text-[#52525B]">
              Page <span className="font-semibold text-[#09090B]">{page}</span> / {totalPages}
            </span>
            <Button size="sm" variant="outline" disabled={page >= totalPages || loading} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="h-7 text-[11.5px] rounded-sm" data-testid="admin-listings-next">
              Next <ChevronRight size={12} />
            </Button>
            <Button size="sm" variant="outline" disabled={page >= totalPages || loading} onClick={() => setPage(totalPages)} className="h-7 text-[11.5px] rounded-sm" data-testid="admin-listings-last">
              Last
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterChip({ children, onClear }) {
  return (
    <button onClick={onClear} className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#0055FF] bg-[#EFF6FF] border border-[#BFDBFE] hover:bg-[#DBEAFE] rounded-full px-2.5 py-0.5">
      {children} <X size={10} />
    </button>
  );
}
